import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';

interface TransferRow {
  id: string;
  status: string;
  created_at: string;
  to_email: string;
  to_name: string;
  reason: string;
  from_user_id: string;
  from_user?: {
    id: string;
    email: string;
  };
  ticket: {
    id: string;
    ticket_number: number;
    guest_name?: string;
    guest_email?: string;
    booking: {
      id: string;
      user?: {
        email: string;
      };
      experience: {
        id: string;
        title: string;
        creator?: {
          business_name: string;
          contact_email: string;
        };
      };
    };
  };
}

export function AdminTransferApprovals() {
  const [transfers, setTransfers] = useState<TransferRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTransfers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('ticket_transfers')
        .select(`
          *,
          from_user:users!ticket_transfers_from_user_id_fkey (
            id,
            email
          ),
          ticket:tickets!ticket_transfers_ticket_id_fkey (
            id,
            ticket_number,
            guest_name,
            guest_email,
            booking:bookings!tickets_booking_id_fkey (
              id,
              user:users!bookings_user_id_fkey (
                email
              ),
              experience:experiences!bookings_experience_id_fkey (
                id,
                title,
                creator:creator_profiles!experiences_creator_profile_id_fkey (
                  business_name,
                  contact_email
                )
              )
            )
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setTransfers(data as any);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transfers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransfers();
  }, []);

  const handleDecision = async (id: string, approve: boolean) => {
    try {
      await supabase
        .from('ticket_transfers')
        .update({ 
          status: approve ? 'approved' : 'rejected', 
          reviewed_at: new Date().toISOString(),
          reviewed_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', id);

      // If approved, complete the transfer
      if (approve) {
        const response = await fetch('/.netlify/functions/complete-ticket-transfer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transferId: id })
        });

        const result = await response.json();
        console.log('Transfer completion result:', result);
      }

      loadTransfers();
    } catch (err) {
      console.error('Failed to process transfer decision:', err);
    }
  };

  if (loading) return <LoadingSpinner />;
  
  if (error) {
    return (
      <div className="bg-red-500/10 rounded-lg p-6">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (transfers.length === 0) {
    return (
      <div className="bg-earth-800/50 rounded-lg p-8 text-center">
        <div className="max-w-sm mx-auto">
          <svg className="w-16 h-16 mx-auto text-sand-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sand-400 text-lg mb-2">All caught up!</p>
          <p className="text-sand-500 text-sm">No pending transfer requests at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h2 className="text-2xl font-display text-sand-100 mb-2">Ticket Transfer Requests</h2>
        <p className="text-sand-400">Review and approve ticket transfers between users.</p>
      </div>
      
      {transfers.map((t) => (
        <div key={t.id} className="bg-earth-800/50 p-6 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <h3 className="text-sand-100 font-medium mb-2">Transfer Details</h3>
              <p className="text-sand-300 text-sm mb-1">
                <span className="text-sand-500">Experience:</span> {t.ticket.booking.experience.title}
              </p>
              <p className="text-sand-300 text-sm mb-1">
                <span className="text-sand-500">Ticket:</span> #{t.ticket.ticket_number}
              </p>
              <p className="text-sand-300 text-sm mb-1">
                <span className="text-sand-500">Creator:</span> {t.ticket.booking.experience.creator?.business_name || 'Unknown'}
              </p>
              <p className="text-sand-300 text-sm">
                <span className="text-sand-500">Requested:</span> {new Date(t.created_at).toLocaleString()}
              </p>
            </div>
            
            <div>
              <h3 className="text-sand-100 font-medium mb-2">Transfer Parties</h3>
              <p className="text-sand-300 text-sm mb-1">
                <span className="text-sand-500">From:</span> {t.from_user?.email || 'Unknown'}
              </p>
              <p className="text-sand-300 text-sm mb-1">
                <span className="text-sand-500">To:</span> {t.to_name || 'Unknown'} ({t.to_email})
              </p>
              {t.reason && (
                <p className="text-sand-300 text-sm mt-2">
                  <span className="text-sand-500">Reason:</span> {t.reason}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t border-earth-700">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => handleDecision(t.id, false)}
              className="text-red-400 border-red-400 hover:bg-red-400/10"
            >
              Reject
            </Button>
            <Button 
              size="sm" 
              onClick={() => handleDecision(t.id, true)}
              className="bg-green-600 hover:bg-green-700"
            >
              Approve Transfer
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
} 